/* Qualified business intent only. Official apps retain all native ecommerce events. */
(() => {
  'use strict';
  function qualify({operation, before, after, currency}, variants) {
    if (currency !== 'AUD' || !operation || !Array.isArray(before) || !Array.isArray(after)) return 0;
    const plain = item => item && variants.includes(Number(item.variant_id)) &&
      Number.isSafeInteger(item.quantity) && item.quantity > 0 &&
      Object.keys(item.properties || {}).length === 0 && !item.selling_plan_allocation;
    if (after.length !== 1 || !after.every(plain) || !before.every(plain) ||
        Number(after[0].variant_id) !== operation.targetId) return 0;
    if (operation.intent === 'add' && before.length === 0) return after[0].quantity;
    // Contract fixture for a future checked quantity UI; today's cart never issues this command.
    if (operation.intent === 'increase_quantity' && before.length === 1 &&
        Number(before[0].variant_id) === operation.targetId) {
      return Math.max(0, after[0].quantity - before[0].quantity);
    }
    return 0; // Including replacements, their intermediate adds, and no-ops.
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {qualify};
  if (typeof window === 'undefined' || window.RevivaCartGoal) return;
  let config;
  try {config = JSON.parse(document.querySelector('script[data-reviva-cart-goal]')?.dataset.revivaCartGoal || '{}');}
  catch (_) {return;}
  if (config.version !== 1 || typeof config.enabled !== 'boolean' ||
      config.eventName !== 'reviva_qualified_add_to_cart' || config.currency !== 'AUD' ||
      !Number.isSafeInteger(config.taboolaAccountId) || config.taboolaAccountId <= 0 ||
      !Array.isArray(config.variantIds) || config.variantIds.length !== 3 ||
      !config.variantIds.every(id => Number.isSafeInteger(id) && id > 0) ||
      config.pendingExpiryMs !== 600000 || config.attemptRetentionMs !== 86400000 ||
      location.hostname !== config.host || window.Shopify?.shop !== config.shop) return;

  const journalKey = 'reviva-qualified-cart-goal-v1';
  const ownerKey = 'reviva-qualified-cart-goal-owner-v1';
  const lockName = 'reviva-cart-selection';
  const consumed = new WeakSet();
  let timer = null, draining = false, retry = false, needsPurge = false;
  const uuid = () => window.crypto.randomUUID();
  function allowed() {
    try {
      const p = window.Shopify.customerPrivacy;
      return config.enabled && p.marketingAllowed() === true && p.saleOfDataAllowed() === true;
    } catch (_) {return false;}
  }
  const ready = () => typeof window._tfa?.push === 'function';
  const payload = quantity => ({notify:'event', name:config.eventName,
    id:config.taboolaAccountId, quantity, currency:config.currency});
  function cancelTimer() {
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
  }
  function withLock(action) {
    if (!navigator.locks) return Promise.resolve();
    return navigator.locks.request(lockName, {mode:'exclusive'}, action).catch(() => {});
  }
  function purgeLocked() {
    let success = true;
    // Deletion, not tracking-storage inspection, is allowed on withdrawal/disable.
    try {window.localStorage.removeItem(journalKey);} catch (_) {success = false;}
    try {window.sessionStorage.removeItem(ownerKey);} catch (_) {success = false;}
    needsPurge = !success;
    return success;
  }
  function purge() {
    needsPurge = true; retry = false; cancelTimer();
    return withLock(purgeLocked);
  }
  const keysAre = (obj, keys) => obj && typeof obj === 'object' && !Array.isArray(obj) &&
    Object.keys(obj).sort().join(',') === [...keys].sort().join(',');
  const identifier = value => typeof value === 'string' && /^[a-zA-Z0-9-]{1,80}$/.test(value);
  function parseJournal(raw) {
    if (raw === null) return null;
    const j = JSON.parse(raw);
    if (!keysAre(j, ['version','generation','entries']) || j.version !== 1 || !identifier(j.generation) ||
        !Array.isArray(j.entries) || !j.entries.every(e =>
          keysAre(e, ['id','owner','createdAt','quantity','currency','status']) && identifier(e.id) &&
          identifier(e.owner) && Number.isSafeInteger(e.createdAt) && e.createdAt >= 0 &&
          Number.isSafeInteger(e.quantity) && e.quantity > 0 && e.currency === 'AUD' &&
          ['pending','attempted'].includes(e.status)) ||
        new Set(j.entries.map(e => e.id)).size !== j.entries.length) throw Error('Invalid goal journal');
    return j;
  }
  function parseOwner(raw) {
    if (raw === null) return null;
    const o = JSON.parse(raw);
    if (!keysAre(o, ['generation','id']) || !identifier(o.generation) || !identifier(o.id)) {
      throw Error('Invalid goal owner');
    }
    return o;
  }
  function prune(j) {
    const now = Date.now();
    j.entries = j.entries.filter(e => e.createdAt <= now && now - e.createdAt <
      (e.status === 'pending' ? config.pendingExpiryMs : config.attemptRetentionMs));
  }
  function save(j) {window.localStorage.setItem(journalKey, JSON.stringify(j));}
  function schedule() {
    retry = true;
    if (!allowed() || !navigator.locks) {retry = false; return;}
    cancelTimer();
    void drain();
  }
  async function drain() {
    if (draining || !allowed()) return;
    draining = true;
    try {
      await withLock(() => {
        retry = false;
        if (!allowed() || (needsPurge && !purgeLocked())) return;
        // Always re-read under the same lock as the producer; never replay a cached payload.
        const j = parseJournal(window.localStorage.getItem(journalKey));
        if (!j) return;
        const owner = parseOwner(window.sessionStorage.getItem(ownerKey));
        prune(j);
        if (!j.entries.length) {purgeLocked(); return;}
        save(j);
        if (!owner || owner.generation !== j.generation) return;
        for (const entry of j.entries) {
          if (entry.owner !== owner.id || entry.status !== 'pending') continue;
          if (!allowed()) return;
          if (!ready()) {retry = true; break;}
          entry.status = 'attempted';
          save(j); // Claim BEFORE push; a crash/ambiguous result must not cause a duplicate.
          if (!allowed()) return;
          try {window._tfa.push(payload(entry.quantity));} catch (_) { /* Never retry attempted sends. */ }
        }
      });
    } finally {
      draining = false;
      if (retry && allowed()) timer = window.setTimeout(() => {timer = null; void drain();}, 500);
    }
  }
  // Caller MUST hold reviva-cart-selection and supply only a verified final commit.
  function recordCommitted(commit) {
    const operation = commit?.operation;
    if (!operation || typeof operation !== 'object' || consumed.has(operation)) return;
    consumed.add(operation); // Also consume denied/unknown commands; no retroactive grant replay.
    const quantity = qualify(commit, config.variantIds);
    if (!quantity || !allowed() || (needsPurge && !purgeLocked())) return;
    let journalRaw, ownerRaw;
    try {
      journalRaw = window.localStorage.getItem(journalKey);
      ownerRaw = window.sessionStorage.getItem(ownerKey);
    } catch (_) {
      // Only this NEW, never-durable command may degrade to one in-memory attempt.
      if (allowed() && ready()) {
        try {window._tfa.push(payload(quantity));} catch (_) {}
      }
      return;
    }
    try {
      const j = parseJournal(journalRaw) || {version:1, generation:uuid(), entries:[]};
      let owner = parseOwner(ownerRaw);
      if (!owner || owner.generation !== j.generation) owner = {generation:j.generation, id:uuid()};
      prune(j);
      if (!allowed()) return;
      window.sessionStorage.setItem(ownerKey, JSON.stringify(owner));
      j.entries.push({id:uuid(), owner:owner.id, createdAt:Date.now(), quantity,
        currency:'AUD', status:'pending'});
      save(j);
      // Do not await a nested lock acquisition, tag readiness, or network delivery.
      schedule();
    } catch (_) { /* Malformed/quota/ambiguous persistence: no uncoordinated fallback/retry. */ }
  }
  window.RevivaCartGoal = Object.freeze({recordCommitted});
  function privacyChanged() {
    if (allowed()) schedule();
    else void purge();
  }
  document.addEventListener('visitorConsentCollected', privacyChanged);
  window.addEventListener('pageshow', () => {if (allowed()) schedule();});
  window.addEventListener('load', () => {if (allowed()) schedule();});
  window.addEventListener('storage', event => {
    if (event.key === journalKey && event.newValue === null) {retry = false; cancelTimer();}
  });
  if (!config.enabled) {void purge(); return;}
  if (window.Shopify.customerPrivacy) privacyChanged();
  else {
    try {
      window.Shopify.loadFeatures([{name:'consent-tracking-api', version:'0.1'}], error => {
        if (error) {void purge(); return;}
        privacyChanged();
      });
    } catch (_) { /* Unknown permission: no send or tracking-storage reads. */ }
  }
})();
